import { PayoutMethodItemMethod, type TaxInfo, TaxInfoType } from "@/api/__generated__/models";
import { ExternalLink } from "@/components/external-link";
import { PAYOUT_METHODS, type SUPPORTED_PAYOUT_METHOD_TYPE } from "@/constants/payouts";
import { getAssetsUrl } from "@/constants/url";
import { useOpenTrolley } from "@/routes/payout/hooks/use-open-trolley";
import { usePayoutMethods } from "@/routes/payout/hooks/use-payout-methods";
import { usePayoutTaxInfo } from "@/routes/payout/hooks/use-tax-info";
import {
  Alert,
  Badge,
  Button,
  List,
  ListItem,
  ShadowBox,
  Thumbnail,
  ThumbnailImage,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Typography,
  cn,
} from "@backpac/monica";
import { useMemo } from "react";
import { Temporal } from "temporal-polyfill";

export function PayoutMethodsTaxContents() {
  const { taxInfo } = usePayoutTaxInfo();
  const {
    data: { payoutMethods },
  } = usePayoutMethods();
  const hasPayoutMethods = payoutMethods.length > 0;
  return (
    <section>
      {hasPayoutMethods && taxInfo == null ? (
        <Alert theme="red">
          To receive payouts, please submit your tax form as required for tax reporting purposes.
        </Alert>
      ) : null}

      <div className="flex flex-col gap-8 mt-6">
        <PayoutMethodContainer />
        <PayoutTaxContainer />
      </div>
    </section>
  );
}

function MyPayoutMethods() {
  const {
    data: { payoutMethods },
  } = usePayoutMethods();
  const openTrolley = useOpenTrolley();

  const orderedPayoutMethods = useMemo(() => {
    return payoutMethods.sort((a, b) => {
      // Primary payout method should be first
      if (a.isPrimary !== b.isPrimary) {
        return a.isPrimary ? -1 : 1;
      }
      // Then sort by createdAt
      return a.createdAt.localeCompare(b.createdAt);
    });
  }, [payoutMethods]);

  return (
    <div className="flex flex-col gap-6">
      <PayoutMethodsTaxContents.Box
        rightAddon={
          <div>
            <Button variant="outlined" size="md" onClick={openTrolley}>
              Edit
            </Button>
          </div>
        }
      >
        {orderedPayoutMethods.map((payoutMethod) => {
          switch (payoutMethod.method) {
            case PayoutMethodItemMethod.BANK_TRANSFER:
              return (
                <BankTransferCard
                  key={payoutMethod.createdAt}
                  holderName={payoutMethod.bankTransferAccountHolder ?? ""}
                  bankName={payoutMethod.bankTransferBankName ?? ""}
                  accountNumber={payoutMethod.bankTransferAccountNum ?? ""}
                  isActive={payoutMethod.isPrimary}
                />
              );
            case PayoutMethodItemMethod.PAYPAL:
              return (
                <PaypalCard
                  key={payoutMethod.createdAt}
                  paypalEmail={payoutMethod.paypalEmailAddress ?? ""}
                  isActive={payoutMethod.isPrimary}
                />
              );
            default:
              throw new Error(`Unknown payout method: ${payoutMethod.method}`);
          }
        })}
      </PayoutMethodsTaxContents.Box>
    </div>
  );
}

interface BankTransferCardProps {
  holderName: string;
  accountNumber: string;
  bankName: string;
  isActive: boolean;
}

function BankTransferCard({ holderName, bankName, accountNumber, isActive: active }: BankTransferCardProps) {
  return (
    <div className="flex flex-row gap-4">
      <Thumbnail>
        <ThumbnailImage src={getAssetsUrl("payout/bank.png")} />
      </Thumbnail>
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <PayoutMethodsTaxContents.PayoutMethodTitle payoutMethod="bank">
            Bank Transfer
          </PayoutMethodsTaxContents.PayoutMethodTitle>
          <Badge theme={active ? "green" : "grey"} withIcon={false}>
            {active ? "Active" : "Inactive"}
          </Badge>
        </div>
        <Typography size="paragraphXs" color="textMuted">
          {bankName} - {maskAccountNumber(accountNumber, 4)}
        </Typography>
        <Typography size="paragraphXs" color="textMuted">
          {holderName}
        </Typography>
      </div>
    </div>
  );
}

function maskAccountNumber(accountNumber: string, revealCount = 4): string {
  if (!accountNumber || accountNumber.length <= revealCount) {
    return accountNumber;
  }
  return accountNumber.slice(-revealCount);
}

interface PaypalCardProps {
  paypalEmail: string;
  isActive: boolean;
}

function PaypalCard({ paypalEmail, isActive }: PaypalCardProps) {
  return (
    <div className="flex flex-row gap-4">
      <Thumbnail>
        <ThumbnailImage src={getAssetsUrl("payout/paypal.png")} />
      </Thumbnail>
      <div className="flex flex-col gap-[6px]">
        <div className="flex items-center gap-2">
          <PayoutMethodsTaxContents.PayoutMethodTitle payoutMethod="paypal">
            PayPal
          </PayoutMethodsTaxContents.PayoutMethodTitle>
          <Badge theme={isActive ? "green" : "grey"} withIcon={false}>
            {isActive ? "Active" : "Inactive"}
          </Badge>
        </div>
        <Typography size="paragraphXs" color="textMuted">
          {paypalEmail}
        </Typography>
      </div>
    </div>
  );
}

function TaxInformation({ taxInfo }: { taxInfo?: TaxInfo | null }) {
  const openTrolley = useOpenTrolley();
  const isW9 = taxInfo?.type === TaxInfoType["W-9"];

  const statusContents = useMemo(() => {
    return {
      PENDING: {
        badge: "Under review",
        badgeColor: "orange" as const,
        tooltip: isW9
          ? "Your tax form is under review and TIN validation is processed every Tuesday. You’ll be notified if there’s any issue."
          : "Your tax form is under review and typically approved within a few business days. You’ll be notified if there’s any issue.",
      },
      REVIEWED: {
        badge: "Reviewed",
        badgeColor: "green" as const,
        tooltip: "Approved. You’re all set to withdraw your balance.",
      },
      VOIDED: {
        badge: "Voided",
        badgeColor: "red" as const,
        tooltip: "Invalid form. A new tax form must be submitted..",
      },
    }[taxInfo?.status ?? "PENDING"];
  }, [taxInfo, isW9]);

  return taxInfo == null ? (
    <PayoutMethodsTaxContents.Box>
      <div className="flex flex-1 flex-col gap-6 items-start justify-between sm:flex-row sm:items-center">
        <div className="flex flex-col gap-2">
          <Typography size="sm" color="textPrimary" weight={600}>
            W-9, W-8BEN, or W-8BEN-E
          </Typography>
          <div className="flex flex-col gap-[6px]">
            <Typography size="xs" color="textMuted">
              Not sure how to fill out your tax form?
            </Typography>
            <ExternalLink
              to="https://help.backpac.app/Choose-your-form-21f56f70dfd080419ca6f4c3da3d6160?source=copy_link"
              className="underline"
            >
              <Typography size="xs" color="textPrimary" weight={700}>
                Check tax form guide
              </Typography>
            </ExternalLink>
          </div>
        </div>
        <div>
          <Button variant="black" size="md" onClick={openTrolley}>
            Submit tax form
          </Button>
        </div>
      </div>
    </PayoutMethodsTaxContents.Box>
  ) : (
    <PayoutMethodsTaxContents.Box
      rightAddon={
        <div>
          <Button variant="outlined" size="md" onClick={openTrolley}>
            Edit
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-[6px]">
        <div className="flex items-center gap-1">
          <Typography size="sm" color="textPrimary" weight={600}>
            {taxInfo.type}
          </Typography>
          <Tooltip>
            <TooltipTrigger>
              <Badge theme={statusContents.badgeColor}>{statusContents.badge}</Badge>
            </TooltipTrigger>
            <TooltipContent className="text-left">{statusContents.tooltip}</TooltipContent>
          </Tooltip>
        </div>
        <div className="flex items-center gap-2">
          <Typography size="paragraphXs" color="textMuted">
            Submitted on{" "}
            {taxInfo.submittedAt
              ? Temporal.Instant.from(taxInfo.submittedAt)
                  .toZonedDateTimeISO(Temporal.Now.timeZoneId())
                  .toLocaleString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
              : "N/A"}
          </Typography>
        </div>
      </div>
    </PayoutMethodsTaxContents.Box>
  );
}

PayoutMethodsTaxContents.Box = function PayoutMethodsContentsBox({
  className,
  children,
  rightAddon,
}: { className?: string; children: React.ReactNode; rightAddon?: React.ReactNode }) {
  return (
    <ShadowBox className={cn("flex flex-col p-8 gap-6 sm:flex-row sm:justify-between sm:items-center", className)}>
      <div className="flex flex-1 flex-col gap-6">{children}</div>
      {rightAddon}
    </ShadowBox>
  );
};

PayoutMethodsTaxContents.PayoutMethodTitle = function PayoutMethodTitle({
  children,
  payoutMethod,
}: {
  children: React.ReactNode;
  payoutMethod: SUPPORTED_PAYOUT_METHOD_TYPE;
}) {
  const method = PAYOUT_METHODS[payoutMethod];

  return (
    <Tooltip>
      <TooltipTrigger>
        <Typography
          size="sm"
          color="textPrimary"
          weight={600}
          className="underline decoration-dotted underline-offset-4 text-left"
        >
          {children}
        </Typography>
      </TooltipTrigger>
      <TooltipContent>
        <div className="flex flex-col gap-2 text-left">
          <List>
            <ListItem>{method.processingTime}</ListItem>
            {method.fees.map((fee, index) => (
              <ListItem key={index} className="text-borderLight text-xs">
                {fee.description}
              </ListItem>
            ))}
          </List>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

// TODO: Remove this
// function authPaypal(workspaceId: string, options?: { onSuccess?: () => void }) {
//   const returnUrl = `${window.location.origin}${routes.OAUTH.paypal}`;

//   const searchParams = new URLSearchParams();
//   searchParams.set("flowEntry", "static");
//   searchParams.set("client_id", env.PAYPAL_CLIENT_ID);
//   searchParams.set("response_type", "code");
//   searchParams.set("scope", "openid email profile address");
//   searchParams.set("redirect_uri", encodeURIComponent(returnUrl));
//   searchParams.set(
//     "state",
//     JSON.stringify({
//       workspaceId,
//       returnUrl: encodeURIComponent(returnUrl),
//     }),
//   );
//   const paypalAuthUrl = `${externalUrls.PAYPAL_URL}/signin/authorize?${searchParams.toString()}`;

//   openWindowPopup(paypalAuthUrl, {
//     title: "PayPal Authentication",
//     width: 500,
//     height: 600,
//     onClose: options?.onSuccess,
//   });
// }

interface PayoutMethodsTaxContentsFieldProps {
  number: number;
  label: string;
  description: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}
PayoutMethodsTaxContents.Field = function PayoutMethodsTaxContentsField({
  number,
  label,
  description,
  children,
  className,
}: PayoutMethodsTaxContentsFieldProps) {
  return (
    <div className={cn("flex gap-6 flex-row", className)}>
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-backgroundOffwhite flex items-center justify-center">
        <Typography size="xs" color="textPrimary" weight={600}>
          {number}
        </Typography>
      </div>
      <div className="flex flex-1 flex-col gap-6 lg:flex-row">
        <div className="flex flex-col gap-2 max-w-[288px]">
          <Typography size="md" weight={600} color="textPrimary">
            {label}
          </Typography>
          {description}
        </div>
        <div className="flex-1">{children}</div>
      </div>
    </div>
  );
};

function PayoutMethodContainer() {
  const {
    data: { payoutMethods },
  } = usePayoutMethods();

  return (
    <PayoutMethodsTaxContents.Field
      number={1}
      label="Payout methods"
      description={
        <div className="flex flex-col gap-4">
          <Typography size="paragraphXs" color="textMuted">
            Select your preferred method. You can receive payments via Paypal or bank transfer.
          </Typography>
        </div>
      }
    >
      {payoutMethods.length === 0 ? <PayoutMethodRegister /> : <MyPayoutMethods />}
    </PayoutMethodsTaxContents.Field>
  );
}

function PayoutMethodRegister() {
  const openTrolley = useOpenTrolley();

  return (
    <section className="flex flex-col gap-6 w-full">
      <PayoutMethodsTaxContents.Box
        rightAddon={
          <div>
            <Button variant="black" onClick={openTrolley}>
              Add payout method
            </Button>
          </div>
        }
      >
        <>
          <div className="flex flex-row gap-4">
            <Thumbnail>
              <ThumbnailImage src={getAssetsUrl(PAYOUT_METHODS.paypal.imageUrl)} />
            </Thumbnail>
            <div className="flex flex-col gap-[6px]">
              <Typography size="sm" color="textPrimary" weight={600}>
                {PAYOUT_METHODS.paypal.name}
              </Typography>
              <Typography size="paragraphXs" color="textMuted" asChild>
                <List>
                  <ListItem>{PAYOUT_METHODS.paypal.processingTime}</ListItem>
                  {PAYOUT_METHODS.paypal.fees.map((fee, index) => (
                    <ListItem key={index} className={"mt-1"}>
                      {fee.description}
                    </ListItem>
                  ))}
                </List>
              </Typography>
            </div>
          </div>
          <div className="flex flex-row gap-4">
            <Thumbnail>
              <ThumbnailImage src={getAssetsUrl(PAYOUT_METHODS.bank.imageUrl)} />
            </Thumbnail>
            <div className="flex flex-col gap-[6px]">
              <Typography size="sm" color="textPrimary" weight={600}>
                {PAYOUT_METHODS.bank.name}
              </Typography>
              <Typography size="paragraphXs" color="textMuted" asChild>
                <List>
                  <ListItem>{PAYOUT_METHODS.bank.processingTime}</ListItem>
                  {PAYOUT_METHODS.bank.fees.map((fee, index) => (
                    <ListItem key={index} className={"mt-1"}>
                      {fee.description}
                    </ListItem>
                  ))}
                </List>
              </Typography>
            </div>
          </div>
        </>
      </PayoutMethodsTaxContents.Box>
    </section>
  );
}

function PayoutTaxContainer() {
  const { taxInfo } = usePayoutTaxInfo();

  return (
    <PayoutMethodsTaxContents.Field
      number={2}
      label="Tax forms"
      description={
        <Typography size="paragraphXs" color="textMuted">
          We securely collect your tax information to verify your tax residency. We don't charge or withhold any tax.
          The form simply confirms where you or your business legally operate. Your submission is reviewed within 24
          hours, and payouts become available once it's approved.
        </Typography>
      }
    >
      <TaxInformation taxInfo={taxInfo} />
    </PayoutMethodsTaxContents.Field>
  );
}
